import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, CalendarCheck, CheckCircle2, Clock4, HelpCircle, AlertTriangle, Siren, HeartHandshake } from "lucide-react";
import api, { fmtDate } from "../../lib/api";
import { Loading, StatCard, PageHeader, StatusPill } from "../../components/Shared";

function ActionTile({ label, count, to, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`rounded-2xl border p-4 transition-shadow hover:shadow-md ${count ? "border-amber-300 bg-amber-50" : "border-stone-200 bg-white"}`}>
      <p className="font-heading text-2xl font-extrabold text-stone-900">{count}</p>
      <p className="text-xs font-semibold text-stone-500">{label} →</p>
    </Link>
  );
}

function ActionList({ title, items, empty, testid }) {
  return (
    <div className="p1-card p-5" data-testid={testid}>
      <h2 className="font-heading text-lg font-semibold text-stone-900">{title}</h2>
      <ul className="mt-3 space-y-2">
        {items.length === 0 && <p className="text-sm text-stone-400">{empty}</p>}
        {items.map((i) => (
          <li key={i.id}>
            <Link to={i.to} data-testid={`${testid}-item-${i.id}`}
              className="block rounded-xl border border-stone-100 p-3 text-sm transition-colors hover:border-emerald-700 hover:bg-emerald-50/40">
              <p className="font-semibold text-stone-800">{i.main}</p>
              <p className="text-xs text-stone-400">{i.sub}</p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminToday() {
  const [d, setD] = useState(null);
  const [inbox, setInbox] = useState([]);
  const load = () => api.get("/admin/today").then((r) => setD(r.data));
  useEffect(() => { load().catch(() => setD({ metrics: {} })); api.get("/admin/notifications/inbox").then((r) => setInbox(r.data)).catch(() => {}); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!d) return <Loading />;
  const m = d.metrics || {};
  return (
    <div data-testid="admin-today">
      <PageHeader title="Today — Operations Overview" sub="Live command centre for ParentOne Jaipur operations." />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Users} label="Active families" value={m.active_families ?? 0} testid="metric-families" />
        <StatCard icon={CalendarCheck} label="Visits today" value={`${m.completed_today ?? 0}/${m.scheduled_today ?? 0}`} testid="metric-visits" />
        <StatCard icon={Clock4} label="Late / missed" value={m.late_missed ?? 0} tone={m.late_missed ? "amber" : "default"} testid="metric-late" />
        <StatCard icon={HelpCircle} label="Open requests" value={m.open_requests ?? 0} testid="metric-requests" />
        <StatCard icon={AlertTriangle} label="Urgent incidents" value={m.urgent_incidents ?? 0} tone={m.urgent_incidents ? "amber" : "default"} testid="metric-incidents" />
        <StatCard icon={Siren} label="Emergency cases" value={m.emergency_cases ?? 0} tone={m.emergency_cases ? "red" : "default"} testid="metric-emergencies" />
        <StatCard icon={HeartHandshake} label="Available CMs" value={m.available_care_managers ?? 0} testid="metric-cms" />
        <StatCard icon={CheckCircle2} label="Visit completion" value={m.scheduled_today ? `${Math.round(100 * (m.completed_today || 0) / m.scheduled_today)}%` : "—"} testid="metric-completion" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="action-tiles">
        <ActionTile label="New enquiries" count={m.new_leads ?? 0} to="/app/admin/leads" testid="action-new-leads" />
        <ActionTile label="Assessments pending" count={m.assessments_pending ?? 0} to="/app/admin/customers" testid="action-assessments-pending" />
        <ActionTile label="Assessments scheduled" count={m.assessments_scheduled ?? 0} to="/app/admin/customers" testid="action-assessments-scheduled" />
        <ActionTile label="Awaiting payment" count={m.awaiting_payment ?? 0} to="/app/admin/customers" testid="action-awaiting-payment" />
        <ActionTile label="Awaiting CM assignment" count={m.awaiting_cm ?? 0} to="/app/admin/customers" testid="action-awaiting-cm" />
      </div>

      {inbox.length > 0 && (
        <div className="p1-card mt-4 p-5" data-testid="action-inbox">
          <h2 className="font-heading text-lg font-semibold text-stone-900">
            Action Required <span className="ml-1 rounded-full bg-terracotta px-2 py-0.5 text-xs font-bold text-white">{inbox.length}</span>
          </h2>
          <ul className="mt-3 space-y-2">
            {inbox.map((n) => (
              <li key={n.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-100 p-3" data-testid={`inbox-item-${n.id}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-stone-800">
                    {n.priority === "emergency" && <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">SOS</span>}
                    {n.priority === "high" && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">HIGH</span>}
                    {n.title}
                  </p>
                  <p className="text-xs text-stone-400">{n.body} · {n.created_at?.slice(0, 16).replace("T", " ")}</p>
                </div>
                <div className="flex gap-2">
                  {n.action_url && <Link to={n.action_url} className="rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 hover:border-emerald-700" data-testid={`inbox-open-${n.id}`}>{n.action_label || "Open"}</Link>}
                  <button
                    onClick={async () => { try { await api.post(`/admin/notifications/${n.id}/ack`); setInbox((cur) => cur.filter((x) => x.id !== n.id)); } catch { /* role may be view-only */ } }}
                    className="rounded-full bg-emerald-900 px-3 py-1 text-xs font-bold text-white" data-testid={`inbox-ack-${n.id}`}>Acknowledge</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.emergencies?.length > 0 && (
        <Link to="/app/admin/emergency" className="mt-6 flex items-center justify-between rounded-2xl border-2 border-red-400 bg-red-50 p-5" data-testid="emergency-banner">
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-red-800"><Siren className="h-6 w-6" /> {d.emergencies.length} open emergency case(s) — action required</p>
          <span className="rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white">Open console →</span>
        </Link>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="p1-card p-5">
          <h2 className="font-heading text-lg font-semibold text-stone-900">Open incidents</h2>
          <ul className="mt-3 space-y-2">
            {(d.incidents || []).length === 0 && <p className="text-sm text-stone-400">No open incidents.</p>}
            {(d.incidents || []).map((i) => (
              <li key={i.id}>
                <Link to="/app/admin/cases" className="flex items-center justify-between rounded-xl border border-stone-100 p-3 text-sm transition-colors hover:border-emerald-700 hover:bg-emerald-50/40" data-testid={`incident-row-${i.id}`}>
                  <span className="capitalize text-stone-700">{i.category.replace(/_/g, " ")} — {i.description?.slice(0, 60)}</span>
                  <StatusPill status={i.severity} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="p1-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Service request queue</h2>
            <Link to="/app/admin/requests" className="text-xs font-bold text-emerald-900" data-testid="requests-view-all">View all →</Link>
          </div>
          <ul className="mt-3 space-y-2">
            {(d.requests || []).length === 0 && <p className="text-sm text-stone-400">Queue is clear.</p>}
            {(d.requests || []).map((r) => (
              <li key={r.id}>
                <Link to={`/app/admin/requests/${r.id}`} className="flex items-center justify-between rounded-xl border border-stone-100 p-3 text-sm transition-colors hover:border-emerald-700 hover:bg-emerald-50/40" data-testid={`request-row-${r.id}`}>
                  <span className="text-stone-700">{r.type} · <span className="text-xs text-stone-400">{r.family_name || ""} · {fmtDate(r.created_at)}</span></span>
                  <StatusPill status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <ActionList title="New enquiries" testid="list-new-leads" empty="No new enquiries."
          items={(d.new_leads_list || []).map((l) => ({ id: l.id, to: `/app/admin/leads?open=${l.id}`, main: l.name, sub: `${l.type === "onboarding_assessment" ? "Assessment booking" : "Contact form"} · ${l.phone || l.email || "—"}` }))} />
        <ActionList title="Assessments pending" testid="list-assessments-pending" empty="No pending assessments."
          items={(d.assessments_pending_list || []).map((f) => ({ id: f.id, to: `/app/admin/customers?id=${f.id}`, main: f.name, sub: `${f.city || "Jaipur"} · tap to schedule / mark completed` }))} />
        <ActionList title="Families awaiting payment" testid="list-awaiting-payment" empty="No pending payments."
          items={(d.awaiting_payment_list || []).map((s) => ({ id: s.id, to: `/app/admin/customers?id=${s.family_id}`, main: s.family_name, sub: `${s.plan_name} · ₹${(s.plan_price || 0).toLocaleString("en-IN")}/mo` }))} />
        <ActionList title="Awaiting CM assignment" testid="list-awaiting-cm" empty="All active families have a Care Manager."
          items={(d.awaiting_cm_list || []).map((f) => ({ id: f.id, to: `/app/admin/customers?id=${f.id}&assign=1`, main: f.name, sub: `${f.city} · ${(f.status || "").replace(/_/g, " ")}` }))} />
        <ActionList title="Today's visits" testid="list-visits-today" empty="No visits scheduled today."
          items={(d.visits_today || []).map((v) => ({ id: v.id, to: `/app/admin/visits/${v.id}`, main: `${v.parent_name} · ${v.cm_name}`, sub: (v.status || "").replace(/_/g, " ") }))} />
      </div>
    </div>
  );
}
